<template>
  <!-- Table Container Card -->
  <b-card
    no-body
  >

    <div class="m-2">

      <!-- Table Top -->
      <b-row>

        <!-- Per Page -->
        <b-col
          cols="12"
          md="6"
          class="d-flex align-items-center justify-content-start mb-1 mb-md-0"
        >
          <TableEntries
            v-model="perPage"
            :options="perPageOptions"
          />
          <Button
            v-if="getUserPermission('CREATE_IBANKING_CUSTOMER_ONBOARDING_CONFIG')"
            :label="$t('Add')"
            size="md"
            @click="showModal('modal-lg')"
          />
        </b-col>

        <!-- Search -->
        <b-col
          cols="12"
          md="6"
          sm="12"
        >
          <div class="d-flex align-items-center justify-content-lg-end">
            <Button
              :label="$t('Filter')"
              size="md"
              icon="FilterIcon"
              show-icon
              class="mr-1"
              @click="showModal('modal-filter')"
            />

            <Button
              :label="$t('Reset Filter')"
              size="md"
              icon="RotateCwIcon"
              show-icon
              @click="resetFilter"
            />
          </div>
        </b-col>
      </b-row>

    </div>

    <b-table
      ref="productProfileRef"
      :items="productProfileList"
      :busy="isBusy"
      :per-page="pageSize"
      :current-page="currentPage"
      responsive
      :fields="translatedTableHeaders"
      primary-key="id"
      show-empty
      :empty-text="$t('No records available')"
      class="position-relative"
    >
      <template #table-busy>
        <div class="text-center text-danger my-2">
          <b-spinner
            class="align-middle"
            variant="primary"
          />
        </div>
      </template>
      <template #cell(SN)="data">
        {{ getOverallIndex(data.index) }}
      </template>

      <template #cell(dateCreated)="data">
        {{ toDateWithYear(data.value) }}
      </template>

      <template #cell(status)="data">
        <b-badge
          pill
          :variant="variantColor(data.value)"
        >
          {{ data.value }}
        </b-badge>
      </template>

      <!-- Column: Actions -->
      <template #cell(actions)="data">
        <div class="text-nowrap">
          <!-- Dropdown -->
          <b-dropdown
            variant="link"
            toggle-class="p-0"
            no-caret
            :right="$store.state.appConfig.isRTL"
          >

            <template #button-content>
              <feather-icon
                icon="MoreVerticalIcon"
                size="16"
                class="align-middle text-body"
              />
            </template>
            <b-dropdown-item
              v-if="getUserPermission('UPDATE_IBANKING_CUSTOMER_ONBOARDING_CONFIG')"
              @click="onSelectProductProfileItem(data.item, $event.target, 'edit')"
            >
              <feather-icon icon="EditIcon" />
              <span class="align-middle ml-50"> {{ $t('Modify') }} </span>
            </b-dropdown-item>

            <b-dropdown-item @click="onSelectProductProfileItem(data.item, $event.target, 'view')">
              <feather-icon icon="EyeIcon" />
              <span class="align-middle ml-50"> {{ $t('View') }} </span>
            </b-dropdown-item>
            <b-dropdown-item
              v-if="getUserPermission(data.item.status === 'Active' ? 'DEACTIVATE_IBANKING_CUSTOMER_ONBOARDING_CONFIG' : 'ACTIVATE_IBANKING_CUSTOMER_ONBOARDING_CONFIG')"
              @click="productProfileStatus(data.item, $event.target)"
            >
              <feather-icon icon="CheckSquareIcon" />
              <span class="align-middle ml-50">{{ data.item.status === 'Active' ? $t('Deactivate') : $t('Activate') }}</span>
            </b-dropdown-item>
          </b-dropdown>
        </div>
      </template>

    </b-table>

    <TablePagination
      v-model="currentPage"
      :from="from"
      :to="to"
      :total-rows="totalEntries"
      :per-page="perPage"
    />

    <!-- modal -->
    <b-modal
      id="modal-lg"
      ref="my-modal"
      :ok-title="buttonLabel"
      :cancel-title="$t('Cancel')"
      cancel-variant="outline-secondary"
      :ok-disabled="isLoading"
      centered
      size="lg"
      no-close-on-backdrop
      :title="modalTitle"
      @ok="handleSubmit"
      @hidden="resetModal"
    >
      <validation-observer ref="casaProfileRules">
        <b-form @submit.prevent="handleSubmit">
          <b-row class="match-height">
            <!-- Product -->
            <b-col md="6">
              <SelectField
                id="product"
                v-model="casaProfileFormData.productCode"
                :label="$t('Product')"
                :options="productOptions"
                :error-name="$t('Product')"
                rules="required"
                :reduce="option => option.value"
                item="label"
                :placeholder="$t('Select')"
                :readonly="isReadonly"
                :set-selected="setProductCode"
              />
            </b-col>
            <!-- Product -->

            <!-- Product Code -->
            <b-col md="6">
              <InputField
                id="product-code"
                v-model="casaProfileFormData.productCode"
                :label="$t('Product Code')"
                :placeholder="$t('Enter product code')"
                type="text"
                :error-name="$t('Product Code')"
                rules="required"
                for="product-code"
                readonly
              />
            </b-col>
            <!-- Product Code -->

            <!-- Account Officer -->
            <b-col md="6">
              <SelectField
                id="account-officer"
                v-model="casaProfileFormData.accountOfficerStaffId"
                :label="$t('Account Officer')"
                :options="accountOfficerOptions"
                :error-name="$t('Account Officer')"
                rules="required"
                :reduce="option => option.value"
                item="label"
                :placeholder="$t('Select')"
                :readonly="isReadonly"
                :set-selected="setAccountOfficerCode"
              />
            </b-col>
            <!-- Account Officer -->

            <!-- Account Officer Code -->
            <b-col md="6">
              <InputField
                id="account-officer-code"
                v-model="casaProfileFormData.accountOfficerStaffId"
                :label="$t('Account Officer Code')"
                :placeholder="$t('Enter account officer code')"
                type="text"
                :error-name="$t('Account Officer Code')"
                rules="required"
                for="account-officer-code"
                readonly
              />
            </b-col>
            <!-- Account Officer Code -->

            <!-- Branch -->
            <b-col md="6">
              <SelectField
                id="branch"
                v-model="casaProfileFormData.branchCode"
                :label="$t('Branch')"
                :options="branchOptions"
                :error-name="$t('Branch')"
                rules="required"
                :reduce="option => option.value"
                item="label"
                :placeholder="$t('Select')"
                :readonly="isReadonly"
                :set-selected="setBranchCode"
              />
            </b-col>
            <!-- Branch -->

            <!-- Branch Code -->
            <b-col md="6">
              <InputField
                id="branch-code"
                v-model="casaProfileFormData.branchCode"
                :label="$t('Branch Code')"
                :placeholder="$t('Enter branch code')"
                type="text"
                :error-name="$t('Branch Code')"
                rules="required"
                for="branch-code"
                readonly
              />
            </b-col>
            <!-- Branch Code -->

            <!-- Account Tier Level-->
            <b-col md="6">
              <SelectField
                id="level-1"
                v-model="casaProfileFormData.tierLevel"
                :label="$t('Account Tier Level')"
                :options="translatedAccountTierLevelOptions"
                :error-name="$t('Account Tier Level')"
                rules="required"
                :reduce="option => option.value"
                item="label"
                :placeholder="$t('Select')"
                :readonly="isReadonly"
              />
            </b-col>
            <!-- Account Tier Level-->
            <!-- Access Level-->
            <b-col md="6">
              <SelectField
                id="level-1"
                v-model="casaProfileFormData.accessLevel"
                :label="$t('Access Level')"
                :options="translatedAccessLevelOptions"
                :error-name="$t('Access Level')"
                rules="required"
                :reduce="option => option.value"
                item="label"
                :placeholder="$t('Select')"
                :readonly="isReadonly"
              />
            </b-col>
            <!-- Access Level-->
            <!-- Default Account Status -->
            <b-col md="6">
              <SelectField
                id="default-account-status"
                v-model="casaProfileFormData.defaultAccountStatus"
                :label="$t('Default Account Status')"
                :options="translatedDefaultAccountStatusOptions"
                :error-name="$t('Default Account Status')"
                rules="required"
                :reduce="option => option.value"
                item="label"
                :placeholder="$t('Select')"
                :readonly="isReadonly"
              />
            </b-col>
            <!-- Default Account Status -->
          </b-row>

        </b-form>
      </validation-observer>
      <template
        v-if="isReadonly"
        #modal-footer="{ }"
      >
        <div />
      </template>
    </b-modal>
    <!-- modal -->

    <!-- Filter modal -->
    <b-modal
      id="modal-filter"
      ref="my-modal"
      :ok-title="$t('Apply')"
      :cancel-title="$t('Cancel')"
      cancel-variant="outline-secondary"
      no-close-on-backdrop
      centered
      size="lg"
      :title="$t('Filters')"
      modal-footer
      @ok="fetchProductProfile"
      @cancel="resetFilters"
    >
      <b-row class="match-height">
        <!-- Product Name -->
        <b-col md="6">
          <SelectField
            id="product-name"
            v-model="filter.productCode"
            :label="$t('Product Name')"
            :placeholder="$t('Select product')"
            :reduce="option => option.value"
            :options="productOptions"
            item="label"
          />
        </b-col>
        <!-- Product Name -->

        <!-- Product Code  -->
        <b-col md="6">
          <InputField
            id="product-code"
            v-model="filter.productCode"
            :label="$t('Product Code')"
            :placeholder="$t('Enter product code')"
            type="text"
          />
        </b-col>
        <!-- Product Code -->

        <!-- Status -->
        <b-col md="6">
          <SelectField
            id="status"
            v-model="filter.status"
            :label="$t('Status')"
            :placeholder="$t('Select')"
            :reduce="option => option"
            :options="statusOptions"
            item="label"
          />
        </b-col>
        <!-- Status -->
      </b-row>
    </b-modal>
    <!-- Filter modal -->
    <FullScreenSpinner :show="showSpinner" />
  </b-card>

</template>

<script>
import {
  BCard, BRow, BCol, BTable, BBadge,
  BDropdown, BModal, BForm, BSpinner, BDropdownItem,
} from 'bootstrap-vue'
import { ValidationObserver } from 'vee-validate'
import { required } from '@validations'
import { modalMixins } from '@core/mixins/modal'
import { tableMixins } from '@core/mixins/table'
import { sweetAlert } from '@core/mixins/sweetAlert'
import { mapActions, mapGetters } from 'vuex'
import { permissionMixin } from '@core/mixins/permission'
import TablePagination from '@/views/components/table/TablePagination.vue'
import TableEntries from '@/views/components/table/TableEntries.vue'
import InputField from '../../../../forms/InputField.vue'
import SelectField from '../../../../forms/SelectField.vue'
import { toDateWithYear } from '@/helpers/formatDate'
import FullScreenSpinner from '@/views/components/spinner/FullScreenSpinner.vue'
import Button from '../../../../components/button/Button.vue'
import { eChannelsPermission } from '@/helpers/permissions'

const accountTierLevelOptions = [
  { label: 'Tier 1', value: 1 },
  { label: 'Tier 2', value: 2 },
  { label: 'Tier 3', value: 3 },
]

const accessLevelOptions = [
  { label: 'Level 1', value: 1 },
  { label: 'Level 2', value: 2 },
  { label: 'Level 3', value: 3 },
  { label: 'Level 4', value: 4 },
  { label: 'Level 5', value: 5 },
]

const defaultAccountStatusOptions = [
  { label: 'Active', value: 'Active' },
  { label: 'PND', value: 'PND' },
  { label: 'PNC', value: 'PNC' },
  { label: 'Frozen', value: 'Frozen' },
]

export default {
  components: {
    BCard,
    BRow,
    BCol,
    BTable,
    BDropdown,
    BSpinner,
    BDropdownItem,
    BModal,
    BBadge,
    BForm,
    InputField,
    SelectField,
    ValidationObserver,
    TablePagination,
    Button,
    TableEntries,
    FullScreenSpinner,
  },
  mixins: [modalMixins, tableMixins, sweetAlert, permissionMixin],
  data() {
    return {
      toDateWithYear,
      sortBy: 'productCode',
      isReadonly: false,
      showSpinner: false,
      buttonLabel: this.$t('Save'),
      modalTitle: this.$t('Create CASA Profile Setup'),
      isSelectLevelThree: false,
      isBusy: false,
      required,
      sortDesc: false,
      tableHeaders: [
        { key: 'SN', label: 'S/N' },
        { key: 'productName', sortable: true, label: 'Product Name' },
        { key: 'productCode', sortable: true, label: 'Product Code' },
        { key: 'branchName', sortable: true, label: 'Branch Name' },
        { key: 'branchCode', sortable: true, label: 'Branch Code' },
        { key: 'accountOfficerName', sortable: true, label: 'Account Officer Name' },
        { key: 'accountOfficerStaffId', sortable: true, label: 'Account Officer Code' },
        { key: 'status', sortable: true, label: 'Status' },
        { key: 'actions', label: 'Actions' },
      ],
      casaProfileFormData: {
        productCode: '',
        accountOfficerStaffId: '',
        branchCode: '',
        tierLevel: '',
        accessLevel: '',
        defaultAccountStatus: '',
        id: '',
      },
      filter: {
        productCode: '',
        status: '',
      },
      productProfileList: [],
      // defaultAccountStatusOptions: [
      //   { label: 'Active', value: 'Active' },
      //   { label: 'PND', value: 'PND' },
      //   { label: 'PNC', value: 'PNC' },
      //   { label: 'Frozen', value: 'Frozen' },
      // ],

      productOptions: [],
      accountOfficerOptions: [],
      // accountTierLevelOptions: [
      //   { label: 'Tier 1', value: 1 },
      //   { label: 'Tier 2', value: 2 },
      //   { label: 'Tier 3', value: 3 },
      // ],
      // accessLevelOptions: [
      //   { label: 'Level 1', value: 1 },
      //   { label: 'Level 2', value: 2 },
      //   { label: 'Level 3', value: 3 },
      //   { label: 'Level 4', value: 4 },
      //   { label: 'Level 5', value: 5 },
      // ],
    }
  },
  computed: {
    ...mapGetters({
      branchOptions: 'operations/branchOptions',
    }),
    translatedTableHeaders() {
      return this.tableHeaders.map(header => ({ ...header, label: header.label ? this.$t(header.label) : header.key }))
    },
    translatedAccountTierLevelOptions() {
      return accountTierLevelOptions.map(header => ({ ...header, label: header.label ? this.$t(header.label) : header.value }))
    },
    translatedAccessLevelOptions() {
      return accessLevelOptions.map(header => ({ ...header, label: header.label ? this.$t(header.label) : header.value }))
    },
    translatedDefaultAccountStatusOptions() {
      return defaultAccountStatusOptions.map(header => ({ ...header, label: header.label ? this.$t(header.label) : header.value }))
    },
  },
  watch: {
    currentPage(newValue) {
      this.currentPage = newValue
      this.fetchProductProfile()
    },
    perPage(newValue) {
      this.perPage = newValue
      this.fetchProductProfile()
    },
  },
  mounted() {
    this.fetchProductProfile()
    this.fetchCASAProducts()
    this.fetchAccountOfficers()
  },
  methods: {
    ...mapActions({
      getProductProfile: 'eChannels/getProductProfileList',
      getCASAProduct: 'account/getProducts',
      getAccountOfficers: 'account/getAccountOfficerList',
      updateProductProfile: 'eChannels/updateProductProfileAction',
      createProductProfile: 'eChannels/createProductProfileAction',
      updateProductProfileStatus: 'eChannels/updateProductProfileStatus',
    }),
    setProductCode(code) {
      this.casaProfileFormData.productCode = code
      return true
    },
    setAccountOfficerCode(code) {
      this.casaProfileFormData.accountOfficerStaffId = code
      return true
    },
    setBranchCode(code) {
      this.casaProfileFormData.branchCode = code
      return true
    },
    async fetchProductProfile() {
      this.isBusy = true
      this.hideModal('modal-filter')
      try {
        const response = await this.getProductProfile({
          search: this.filter,
          page: this.currentPage,
          limit: this.perPage,
          permission: eChannelsPermission.VIEW_IBANKING_CUSTOMER_ONBOARDING_CONFIG,
        })
        const { status, data } = response
        if (status === 200 && data.data) {
          this.productProfileList = data.data.map(items => ({
            ...items,
            status: items.isActive === true ? 'Active' : 'InActive',
          }))
          this.totalCount = data.recordCount
        } else {
          this.productProfileList = []
          this.totalCount = 0
        }
      } catch (error) {
        this.alertError({ text: error })
      } finally {
        this.isBusy = false
      }
    },
    async fetchAccountOfficers() {
      try {
        const { data, status } = await this.getAccountOfficers({
          search: '',
          page: 0,
          limit: 0,
          permission: eChannelsPermission.VIEW_IBANKING_CUSTOMER_ONBOARDING_CONFIG,
        })

        if (status === 200 && data.data) {
          this.accountOfficerOptions = data.data.filter(item => item.status === 'Active').map(item => ({ label: item.name, value: item.staffID }))
        }
      } catch (error) {
        this.alertError({ text: error })
      }
    },
    async fetchCASAProducts() {
      try {
        const response = await this.getCASAProduct({
          search: { type: 'Savings' },
          page: 0,
          limit: 0,
          permission: eChannelsPermission.VIEW_IBANKING_CUSTOMER_ONBOARDING_CONFIG,
        })
        const { status, data } = response
        if (status === 200 && data.data) {
          this.productOptions = data.data.filter(itemValue => itemValue.status === 'Active').map(item => ({ label: item.name, value: item.code.toString() }))
        }
      } catch (error) {
        this.alertError({ text: error })
      } finally {
        this.isBusy = false
      }
    },
    handleSubmit(bvModalEvt) {
      try {
        bvModalEvt.preventDefault()
        this.$refs.casaProfileRules.validate().then(async success => {
          if (success) {
            const text1 = this.$t('Are you sure you want to')
            const text2 = this.$t('this CASA Profile Setup?')
            this.confirm(this.productProfileAction, { text: `${text1} ${this.buttonLabel} ${text2}` })
          }
        })
      } catch (error) {
        this.handleError(error)
      } finally {
        this.isLoading = false
        this.hideModal('modal-lg')
      }
    },
    async productProfileAction() {
      try {
        this.isLoading = true
        const response = this.buttonLabel === 'Save'
          ? await this.createProductProfile(this.casaProfileFormData)
          : await this.updateProductProfile(this.casaProfileFormData)

        const { status } = response
        if (status === 200) {
          this.fetchProductProfile()
          this.hideModal('modal-lg')
          const text1 = this.$t('Your request to')
          const text2 = this.$t('CASA Profile Setup was successful')
          this.alertSuccess({ text: `${text1} ${this.buttonLabel} ${text2}` })
        }
      } catch (error) {
        this.handleError(error)
      } finally {
        this.isLoading = false
      }
    },
    productProfileStatus(items) {
      const { id } = items
      const action = items.status === 'Active' ? this.$t('Deactivate') : this.$t('Activate')
      const permission = items.status === 'Active' ? eChannelsPermission.DEACTIVATE_IBANKING_CUSTOMER_ONBOARDING_CONFIG : eChannelsPermission.ACTIVATE_IBANKING_CUSTOMER_ONBOARDING_CONFIG

      const updateStatus = async () => {
        try {
          this.showSpinner = true
          const statusValues = {
            id,
          }
          const response = await this.updateProductProfileStatus({
            statusValues, routeName: action, permission,
          })
          if (response.status === 200) {
            this.fetchProductProfile()
            const newText = this.$t('Your request to')
            const text2 = this.$t('CASA Profile Setup was successful')
            this.alertSuccess({ text: `${newText} ${action} ${text2}` })
            this.showSpinner = false
          }
        } catch (error) {
          const newText = this.$t('Your request to')
          const errorText = this.$t('CASA Profile Setup failed. Reason:')
          this.alertError({ text: `${newText} ${action} ${errorText} ${error}` })
        } finally {
          this.showSpinner = false
        }
      }
      const areYouSure = this.$t('Are you sure you want to')
      const text2 = this.$t('this CASA Profile Setup?')
      this.confirm(updateStatus, { title: '', text: `${areYouSure} ${action} ${text2}` })
    },

    async onSelectProductProfileItem(items, activity, val) {
      try {
        this.casaProfileFormData = {
          ...items,
        }
        this.isReadonly = val !== 'edit'
        this.modalTitle = val === 'edit' ? this.$t('Edit CASA Profile Setup') : this.$t('View CASA Profile Setup')
        this.buttonLabel = this.$t('Update')
        this.$root.$emit('bv::show::modal', 'modal-lg')
      } catch (error) {
        this.alertError({ text: error })
      }
    },
    resetFilters() {
      this.filter = { ...null }
    },
    resetFilter() {
      this.filter = { ...null }
      this.fetchProductProfile()
    },
    resetModal() {
      this.casaProfileFormData = { ...null }
      this.isReadonly = false
      this.isLoading = false
      this.modalTitle = this.$t('Create CASA Profile Setup')
      this.buttonLabel = this.$t('Save')
    },
  },
}
</script>
